import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The nearest ancestor that actually scrolls.
 *
 * Walking up rather than assuming `<main>` keeps the hook correct if the table
 * is ever placed inside a drawer or a panel with its own scroll region — the
 * table should fill whichever box is scrolling it.
 */
function findScrollParent(node: HTMLElement): HTMLElement | null {
  let parent = node.parentElement;
  while (parent) {
    const overflowY = getComputedStyle(parent).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') return parent;
    parent = parent.parentElement;
  }
  return null;
}

/**
 * The height of everything BELOW the viewport inside its own surface — the
 * pagination footer, and any second trailing row a surface grows.
 *
 * Measuring only against the scroll container's bottom would let the table
 * grow until the footer was pushed past the container's edge, which is exactly
 * the overflow this hook exists to prevent. The siblings are summed rather
 * than the footer being named, so a surface that grows a bulk bar or a summary
 * line is still measured correctly without this hook knowing what it is.
 */
function trailingSiblingsHeight(node: HTMLElement, surface: HTMLElement): number {
  const nodeBottom = node.getBoundingClientRect().bottom;
  let total = 0;
  for (const sibling of Array.from(surface.children)) {
    if (sibling === node) continue;
    const box = sibling.getBoundingClientRect();
    // Only siblings that start at or below the viewport's bottom edge are
    // "below" it; the tabs row and filter bar sit above and are already
    // excluded by measuring from the viewport's own top.
    if (box.top >= nodeBottom - 1) total += box.height;
  }
  return total;
}

/**
 * The surface's own vertical borders.
 *
 * They are 1px each, and leaving them out made the card exactly 1px taller
 * than the room available — enough to make the scroll container overflow by a
 * single pixel and paint a scrollbar for a page that does not actually scroll.
 * A 1px error is invisible in the layout and very visible as a scrollbar, so
 * it is worth naming.
 */
function surfaceBorderHeight(surface: HTMLElement): number {
  const style = getComputedStyle(surface);
  return (
    (Number.parseFloat(style.borderTopWidth) || 0) +
    (Number.parseFloat(style.borderBottomWidth) || 0)
  );
}

/**
 * Sizes the table viewport to the space actually available.
 *
 * WHY MEASURE INSTEAD OF SUBTRACT
 * -------------------------------
 * The first version of this hook guessed: `window.innerHeight - 160 - 120`.
 * Those two numbers were a hand-count of the chrome above and below the table,
 * and a hand-count is wrong the moment anything changes — a taller page header,
 * a saved-views row that only appears on some pages, a filter bar that wraps to
 * two lines on a narrow screen, the bottom nav's `pb-24` that only exists below
 * `lg`. On a short window the error compounds: the fixed 280px is a much larger
 * fraction of a 555px viewport than of a 1080px one, so the table was squeezed
 * to 275px while the page still overflowed its own scroll container and the
 * section scrolled up under the header.
 *
 * So this measures the real geometry instead. The viewport's own top edge is
 * known (`getBoundingClientRect().top`), and the bottom of the space it may
 * occupy is the bottom of its nearest scrollable ancestor — the shell's
 * `<main>`, which is the ONE scroll container for an ordinary page. The
 * difference is the height the table can actually have, whatever the chrome
 * above it happens to be.
 *
 * WHY A REF AND NOT A PROP
 * The element being sized is the one doing the measuring, so the hook owns a
 * ref the caller attaches to the viewport. Nothing else needs to know the
 * number.
 *
 * WHY THE RESIZE OBSERVER
 * A `window.resize` listener alone misses the common case: the chrome above the
 * table changes height without the window changing at all (a filter bar
 * wrapping, a saved-views row appearing, a banner being dismissed). Observing
 * the scroll container catches those, because its own box is what the table is
 * measured against.
 *
 * jsdom reports every box as 0, which would collapse the table to nothing, so
 * the value is only applied once it is positive — in a test the table simply
 * keeps its natural height.
 */
export function useDynamicTableHeight(options: { enabled?: boolean } = {}): {
  viewportRef: (node: HTMLElement | null) => void;
  viewportStyle: { maxHeight: string } | undefined;
} {
  const { enabled = true } = options;
  const [maxHeight, setMaxHeight] = useState(0);
  const nodeRef = useRef<HTMLElement | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);

  const measure = useCallback(() => {
    const node = nodeRef.current;
    if (!node) return;

    const scrollParent = findScrollParent(node);
    // With no scrollable ancestor the table is in normal flow and should keep
    // its natural height rather than being capped against the window.
    if (!scrollParent) {
      setMaxHeight(0);
      return;
    }

    const nodeTop = node.getBoundingClientRect().top;
    const parentBottom = scrollParent.getBoundingClientRect().bottom;
    // The scroll container's own bottom padding is space the table must not
    // occupy, or the last row would sit under the bottom nav.
    const parentPaddingBottom = Number.parseFloat(getComputedStyle(scrollParent).paddingBottom) || 0;

    const surface = node.parentElement;
    const trailingHeight = surface ? trailingSiblingsHeight(node, surface) : 0;
    const borderHeight = surface ? surfaceBorderHeight(surface) : 0;

    setMaxHeight(
      Math.max(
        0,
        Math.floor(parentBottom - parentPaddingBottom - nodeTop - trailingHeight - borderHeight),
      ),
    );
  }, []);

  const viewportRef = useCallback(
    (node: HTMLElement | null) => {
      nodeRef.current = node;
      observerRef.current?.disconnect();
      observerRef.current = null;

      if (!node || !enabled || typeof ResizeObserver === 'undefined') return;

      const scrollParent = findScrollParent(node);
      const observer = new ResizeObserver(measure);
      // Observing the node catches its own content changing; observing the
      // scroll parent catches the chrome above it changing.
      observer.observe(node);
      if (scrollParent) observer.observe(scrollParent);
      observerRef.current = observer;
      measure();
    },
    [enabled, measure],
  );

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;

    // A window resize changes the scroll container's height, which the
    // ResizeObserver already reports — but a resize that only changes the
    // WIDTH (and so re-wraps the chrome) may not change the container's box,
    // so the listener stays as a backstop.
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [enabled, measure]);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  return { viewportRef, viewportStyle: maxHeight > 0 ? { maxHeight: `${maxHeight}px` } : undefined };
}
